import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Link, useNavigate } from "react-router";
import {
  CHECK_LABELS,
  missingForCompletion,
  outsideLimit,
  PROTOCOL_CATEGORIES,
  PROTOCOL_DISCLAIMER,
  PROTOCOL_DISCLAIMER_SHORT,
  PROTOCOL_TEMPLATES,
  protocolTemplate,
  SIGNATURE_NOTE,
  type CheckValue,
  type ColumnDef,
  type FieldDef,
  type FolderInfo,
  type PhotoRef,
  type ProjectDetail,
  type ProjectPhoto,
  type ProtocolCategory,
  type ProtocolData,
  type ProtocolDetail,
  type ProtocolSummary,
  type ProtocolTemplate,
  type ProtocolValue,
  type SectionDef,
  type Signature,
} from "@handvero/shared";
import { isOffline, queueRequest } from "./offlineQueue";
import { api, errorText } from "../api";
import { BrandingSheet, BrandStrip, ShareSheet, useBranding } from "./ProtocolSharing";
import { EmptyState, Icon, Island, Message, PdfViewer, Sheet, splitNumber, useUnauthorizedRedirect } from "./ui";
import "./protocols.css";

/*
 * Protokolle eines Projekts: aus einer Vorlage anlegen, am PC oder auf dem Handy ausfüllen, auf dem Bildschirm
 * unterschreiben und als PDF im Projektordner ablegen. Entwürfe speichern sich von selbst.
 */

type Row = Record<string, unknown>;

const CAT_SHORT: Record<ProtocolCategory, string> = { pruefung: "PR", arbeitsschutz: "AS", abnahme: "AB" };

function day(iso: string) {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const isSignature = (v: unknown): v is Signature => !!v && typeof v === "object" && typeof (v as Signature).image === "string";

/* ---------- Liste ---------- */

export function ProtocolsView(props: { projectId: string; protocolId?: string; base: string }) {
  if (props.protocolId) return <ProtocolEditor key={props.protocolId} {...props} protocolId={props.protocolId} />;
  return <ProtocolList {...props} />;
}

function ProtocolList({ projectId, base }: { projectId: string; base: string }) {
  const onAuth = useUnauthorizedRedirect();
  const navigate = useNavigate();
  const [list, setList] = useState<ProtocolSummary[] | null>(null);
  const [error, setError] = useState("");
  const [picking, setPicking] = useState(false);
  const [menu, setMenu] = useState<ProtocolSummary | null>(null);
  const [pdf, setPdf] = useState<{ url: string; title: string } | null>(null);
  const branding = useBranding();
  const [editBrand, setEditBrand] = useState(false);
  const [sharing, setSharing] = useState<{ preselect?: string } | null>(null);

  const load = useCallback(() => {
    api
      .get<ProtocolSummary[]>(`/api/projects/${projectId}/protocols`)
      .then(setList)
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [projectId, onAuth]);
  useEffect(load, [load]);

  async function create(body: { templateId?: string; copyFrom?: string }) {
    try {
      const p = await api.post<ProtocolDetail>(`/api/projects/${projectId}/protocols`, body);
      navigate(`${base}/${p.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function remove(p: ProtocolSummary) {
    setMenu(null);
    const text =
      p.status === "draft"
        ? `Entwurf „${p.title}“ endgültig löschen?`
        : `„${p.title}“ aus der Protokoll-Liste entfernen? Das PDF bleibt im Projektordner erhalten.`;
    if (!window.confirm(text)) return;
    try {
      await api.del(`/api/protocols/${p.id}`);
      setList((l) => (l ?? []).filter((x) => x.id !== p.id));
    } catch (err) {
      setError(errorText(err));
    }
  }

  const drafts = (list ?? []).filter((p) => p.status === "draft");
  const done = (list ?? []).filter((p) => p.status === "completed");

  const row = (p: ProtocolSummary) => {
    const tpl = protocolTemplate(p.templateId);
    return (
      <li key={p.id} className="prot-row">
        <Link to={`${base}/${p.id}`} className="row">
          <span className={`row-ico prot-ico ${p.status}`}>{tpl ? CAT_SHORT[tpl.category] : "PR"}</span>
          <span className="row-text">
            <b>{p.title}</b>
            <small>
              {p.status === "draft" ? `Entwurf · ${day(p.updatedAt)}` : `Abgeschlossen · ${day(p.completedAt ?? p.updatedAt)}`}
              {p.createdBy && ` · ${p.createdBy}`}
            </small>
          </span>
          {p.status === "completed" && <Icon name="check" />}
        </Link>
        <button className="ab-btn small prot-more" onClick={() => setMenu(p)} aria-label={`${p.title}: weitere Aktionen`}>
          <Icon name="more" />
        </button>
      </li>
    );
  };

  return (
    <>
      <main className="app-main prot-main">
        <BrandStrip branding={branding} onEdit={() => setEditBrand(true)} />
        <Message>{error}</Message>
        {!list && !error && (
          <div className="skel" role="status" aria-label="Wird geladen">
            <i />
            <i />
          </div>
        )}
        {list?.length === 0 && (
          <EmptyState
            title="Noch keine Protokolle"
            text="Prüfprotokolle, Unterweisungen, Gefährdungsbeurteilungen, Abnahmen und Wartungen entstehen hier aus Vorlagen. Ausfüllen, unterschreiben lassen, fertig: Das PDF landet von selbst im Projektordner."
            words={["Prüfen", "Belegen"]}
          />
        )}
        {drafts.length > 0 && (
          <section className="prot-group">
            <h2 className="prot-h">In Arbeit</h2>
            <ul className="list">{drafts.map(row)}</ul>
          </section>
        )}
        {done.length > 0 && (
          <section className="prot-group">
            <h2 className="prot-h">Abgeschlossen</h2>
            <ul className="list">{done.map(row)}</ul>
          </section>
        )}
        <div className="prot-actions">
          <button className="app-btn prot-new" onClick={() => setPicking(true)}>
            <Icon name="plus" /> Neues Protokoll
          </button>
          {done.length > 0 && (
            <button className="app-btn ghost prot-new" onClick={() => setSharing({})}>
              <Icon name="send" /> An Kunden senden
            </button>
          )}
        </div>
        <p className="prot-legal">{PROTOCOL_DISCLAIMER_SHORT}</p>
      </main>

      <Island>
        <button className="is-tab" onClick={() => setPicking(true)}>
          <Icon name="clipboard" />
          <span>Vorlagen</span>
        </button>
        <button className="is-action" onClick={() => setPicking(true)} aria-label="Neues Protokoll">
          <Icon name="plus" />
        </button>
      </Island>

      {pdf && <PdfViewer url={pdf.url} title={pdf.title} onClose={() => setPdf(null)} />}
      {picking && <TemplatePicker onClose={() => setPicking(false)} onPick={(id) => void create({ templateId: id })} />}
      {menu && (
        <Sheet title={menu.title} onClose={() => setMenu(null)}>
          <div className="menu">
            {menu.fileId && (
              <button
                onClick={() => {
                  setPdf({ url: `/api/files/${menu.fileId}/content`, title: menu.title });
                  setMenu(null);
                }}
              >
                PDF öffnen
              </button>
            )}
            {menu.status === "completed" && menu.fileId && (
              <button
                onClick={() => {
                  setMenu(null);
                  setSharing({ preselect: menu.id });
                }}
              >
                Per E-Mail an Kunden senden
                <small>Ihr Kunde lädt das PDF über einen Link herunter, ohne eigenes Konto.</small>
              </button>
            )}
            <button
              onClick={() => {
                setMenu(null);
                void create({ copyFrom: menu.id });
              }}
            >
              Als Vorlage für ein neues Protokoll nutzen
              <small>Übernimmt alle Angaben, ohne Datum und Unterschriften. Ideal für die nächste Prüfung.</small>
            </button>
            {menu.canDelete && (
              <button className="danger" onClick={() => void remove(menu)}>
                {menu.status === "draft" ? "Entwurf löschen" : "Aus der Liste entfernen"}
              </button>
            )}
          </div>
        </Sheet>
      )}
      {editBrand && branding && <BrandingSheet branding={branding} onClose={() => setEditBrand(false)} />}
      {sharing && <ShareSheet projectId={projectId} protocols={list ?? []} preselect={sharing.preselect} onClose={() => setSharing(null)} />}
    </>
  );
}

function TemplatePicker({ onClose, onPick }: { onClose: () => void; onPick: (id: string) => void }) {
  const [busy, setBusy] = useState(false);
  const cats = Object.keys(PROTOCOL_CATEGORIES) as ProtocolCategory[];
  return (
    <Sheet title="Vorlage wählen" onClose={onClose}>
      <div className="tpl-pick">
        {cats.map((c) => (
          <section key={c}>
            <h3>{PROTOCOL_CATEGORIES[c]}</h3>
            <ul>
              {PROTOCOL_TEMPLATES.filter((t) => t.category === c).map((t) => (
                <li key={t.id}>
                  <button
                    disabled={busy}
                    onClick={() => {
                      setBusy(true);
                      onPick(t.id);
                    }}
                  >
                    <b>{t.name}</b>
                    <small>{t.description}</small>
                    <em>{t.basis.slice(0, 3).join(" · ")}</em>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="prot-legal">{PROTOCOL_DISCLAIMER}</p>
      </div>
    </Sheet>
  );
}

/* ---------- Ausfüllen ---------- */

type SaveState = "saved" | "saving" | "error";

function ProtocolEditor({ projectId, protocolId, base }: { projectId: string; protocolId: string; base: string }) {
  const onAuth = useUnauthorizedRedirect();
  const navigate = useNavigate();
  const [p, setP] = useState<ProtocolDetail | null>(null);
  const [data, setData] = useState<ProtocolData>({});
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  const [save, setSave] = useState<SaveState>("saved");
  const [previewing, setPreviewing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const dirty = useRef<Set<string>>(new Set());
  const titleDirty = useRef(false);
  const dataRef = useRef<ProtocolData>({});
  const timer = useRef<number | undefined>(undefined);
  const inFlight = useRef<Promise<void> | null>(null);
  const titleRef = useRef("");
  titleRef.current = title;

  const template = p ? protocolTemplate(p.templateId) : undefined;
  const branding = useBranding();

  useEffect(() => {
    api
      .get<ProtocolDetail>(`/api/protocols/${protocolId}`)
      .then((d) => {
        setP(d);
        setData(d.data);
        dataRef.current = d.data;
        setTitle(d.title);
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [protocolId, onAuth]);

  /** Neue Werte vom Server übernehmen, ohne gerade getippte Eingaben zu verlieren. */
  const adopt = useCallback((d: ProtocolDetail) => {
    setP(d);
    const merged: ProtocolData = { ...d.data };
    for (const k of dirty.current) merged[k] = dataRef.current[k] ?? null;
    dataRef.current = merged;
    setData(merged);
    if (!titleDirty.current) setTitle(d.title);
  }, []);

  const flush = useCallback(async (): Promise<void> => {
    window.clearTimeout(timer.current);
    if (inFlight.current) await inFlight.current;
    if (!dirty.current.size && !titleDirty.current) return;
    const keys = [...dirty.current];
    const body: { data: Record<string, ProtocolValue>; title?: string } = { data: Object.fromEntries(keys.map((k) => [k, dataRef.current[k] ?? null])) };
    if (titleDirty.current) body.title = titleRef.current.trim() || undefined;
    dirty.current = new Set();
    titleDirty.current = false;
    setSave("saving");
    const run = (async () => {
      try {
        const d = await api.put<ProtocolDetail>(`/api/protocols/${protocolId}`, body);
        adopt(d);
        setSave("saved");
        setError("");
      } catch (err) {
        if (onAuth(err)) return;
        if (isOffline(err)) {
          // Kein Netz: Eingaben auf dem Gerät sichern, sie werden später automatisch gesendet.
          try {
            await queueRequest({ method: "PUT", url: `/api/protocols/${protocolId}`, body, label: "Protokoll" }, true);
            setSave("saved");
            setError("Kein Netz: Ihre Eingaben sind auf dem Gerät gesichert und werden automatisch gesendet, sobald wieder Verbindung besteht.");
            return;
          } catch {
            /* Gerätespeicher nicht verfügbar: dann wie bisher als Fehler zeigen */
          }
        }
        for (const k of keys) dirty.current.add(k);
        setSave("error");
        setError(errorText(err));
      }
    })();
    inFlight.current = run;
    await run;
    inFlight.current = null;
  }, [protocolId, adopt, onAuth]);

  const schedule = useCallback(() => {
    window.clearTimeout(timer.current);
    setSave("saving");
    timer.current = window.setTimeout(() => void flush(), 1000);
  }, [flush]);

  const set = useCallback(
    (key: string, value: ProtocolValue) => {
      dataRef.current = { ...dataRef.current, [key]: value };
      setData(dataRef.current);
      dirty.current.add(key);
      schedule();
    },
    [schedule],
  );

  // Vor dem Verlassen noch speichern; Unterschriften vom Handy erscheinen am PC ohne Neuladen.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") void flush();
    };
    document.addEventListener("visibilitychange", onHide);
    const poll = window.setInterval(() => {
      if (document.visibilityState !== "visible" || dirty.current.size || inFlight.current) return;
      api
        .get<ProtocolDetail>(`/api/protocols/${protocolId}`)
        .then(adopt)
        .catch(() => undefined);
    }, 15_000);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.clearInterval(poll);
      void flush();
    };
  }, [flush, adopt, protocolId]);

  async function preview() {
    await flush();
    setPreviewing(true);
  }

  if (error && !p) {
    return (
      <main className="app-main">
        <Message>{error}</Message>
        <Link className="btn-text" to={base}>
          Zurück zu den Protokollen
        </Link>
      </main>
    );
  }
  if (!p || !template) {
    return (
      <main className="app-main">
        <div className="skel" role="status" aria-label="Wird geladen">
          <i />
          <i />
          <i />
        </div>
      </main>
    );
  }

  if (p.status === "completed") return <CompletedView p={p} template={template} projectId={projectId} base={base} />;

  return (
    <>
      {previewing && <PdfViewer url={`/api/protocols/${protocolId}/pdf`} title={`Vorschau: ${title || p.title}`} onClose={() => setPreviewing(false)} />}
      <main className="app-main prot-main prot-edit">
        <div className="prot-top">
          <Link className="btn-text prot-back" to={base}>
            <Icon name="back" /> Alle Protokolle
          </Link>
          <span className={`prot-save ${save}`} role="status">
            {save === "saved" ? "Gespeichert" : save === "saving" ? "Wird gespeichert …" : "Nicht gespeichert"}
          </span>
        </div>
        <BrandStrip branding={branding} />
        <div className="prot-title">
          <small>{PROTOCOL_CATEGORIES[template.category]}</small>
          <input
            value={title}
            maxLength={160}
            aria-label="Name des Protokolls"
            onChange={(e) => {
              setTitle(e.target.value);
              titleDirty.current = true;
              schedule();
            }}
          />
          <p className="prot-basis">{template.basis.join(" · ")}</p>
        </div>
        <Message>{error}</Message>

        {template.sections.map((s, i) => (
          <SectionCard key={s.title} index={i} section={s} data={data} set={set} projectId={projectId} />
        ))}

        <div className="prot-finish">
          <button className="app-btn ghost" onClick={() => void preview()}>
            <Icon name="eye" /> PDF-Vorschau
          </button>
          <button
            className="app-btn"
            onClick={() => {
              void flush().then(() => setCompleting(true));
            }}
          >
            <Icon name="check" /> Abschließen
          </button>
        </div>
        <p className="prot-legal">{PROTOCOL_DISCLAIMER_SHORT}</p>
      </main>

      <Island>
        <button className="is-tab" onClick={() => void preview()}>
          <Icon name="eye" />
          <span>Vorschau</span>
        </button>
        <button className="is-action" onClick={() => void flush().then(() => setCompleting(true))} aria-label="Protokoll abschließen">
          <Icon name="check" />
        </button>
      </Island>

      {completing && (
        <CompleteSheet
          protocol={p}
          template={template}
          data={data}
          projectId={projectId}
          onClose={() => setCompleting(false)}
          onDone={(d) => {
            setCompleting(false);
            setP(d);
            navigate(`${base}/${d.id}`, { replace: true });
          }}
        />
      )}
    </>
  );
}

function SectionCard(props: {
  index: number;
  section: SectionDef;
  data: ProtocolData;
  set: (key: string, v: ProtocolValue) => void;
  projectId: string;
}) {
  const { section, data, set } = props;
  const checks = section.fields.filter((f) => f.type === "check");
  const open = checks.filter((f) => !data[f.key]);
  return (
    <section className="prot-sec" style={{ "--i": props.index } as React.CSSProperties}>
      <header>
        <span className="prot-num">{String(props.index + 1).padStart(2, "0")}</span>
        <h2>{section.title}</h2>
        {checks.length > 1 && open.length > 0 && (
          <button className="prot-allok" onClick={() => open.forEach((f) => set(f.key, "ok"))}>
            Offene auf i. O.
          </button>
        )}
      </header>
      {section.intro && <p className="prot-intro">{section.intro}</p>}
      <div className="prot-fields">
        {section.fields.map((f) => (
          <Field key={f.key} field={f} value={data[f.key] ?? null} onChange={(v) => set(f.key, v)} projectId={props.projectId} />
        ))}
      </div>
    </section>
  );
}

function LimitNote({ limit, value }: { limit?: FieldDef["limit"]; value: unknown }) {
  if (!limit) return null;
  const bad = outsideLimit(limit, value);
  return <span className={`prot-limit${bad ? " bad" : ""}`}>{bad ? `Außerhalb des Grenzwerts ${limit.label}. Bitte prüfen.` : `Grenzwert ${limit.label}`}</span>;
}

function Field({ field, value, onChange, projectId }: { field: FieldDef; value: ProtocolValue; onChange: (v: ProtocolValue) => void; projectId: string }) {
  const s = typeof value === "string" ? value : "";
  const label = (
    <span className="prot-label">
      {field.label}
      {field.required && <i aria-label="Pflichtangabe"> *</i>}
    </span>
  );
  const hint = field.hint && field.type !== "info" ? <span className="prot-hint">{field.hint}</span> : null;

  switch (field.type) {
    case "info":
      return (
        <div className="prot-info">
          <b>{field.label}</b>
          <p>{field.hint}</p>
        </div>
      );
    case "text":
    case "date":
      return (
        <label className="prot-field">
          {label}
          <input type={field.type} value={s} maxLength={4000} onChange={(e) => onChange(e.target.value)} />
          {hint}
        </label>
      );
    case "number":
      return (
        <label className={`prot-field${outsideLimit(field.limit, s) ? " warn" : ""}`}>
          {label}
          <span className="prot-unit">
            <input inputMode="decimal" value={s} maxLength={40} onChange={(e) => onChange(e.target.value)} />
            {field.unit && <em>{field.unit}</em>}
          </span>
          <LimitNote limit={field.limit} value={s} />
          {hint}
        </label>
      );
    case "textarea":
      return (
        <label className="prot-field wide">
          {label}
          <textarea rows={3} value={s} maxLength={4000} onChange={(e) => onChange(e.target.value)} />
          {hint}
        </label>
      );
    case "choice": {
      const long = (field.options ?? []).some((o) => o.length > 28);
      return (
        <fieldset className={`prot-field prot-choice${long ? " long wide" : ""}`}>
          <legend>
            {field.label}
            {field.required && <i aria-label="Pflichtangabe"> *</i>}
          </legend>
          <div>
            {field.options!.map((o) => (
              <button type="button" key={o} className={s === o ? "on" : undefined} aria-pressed={s === o} onClick={() => onChange(s === o ? "" : o)}>
                {long && <span className="prot-radio" aria-hidden="true" />}
                {o}
              </button>
            ))}
          </div>
          {hint}
        </fieldset>
      );
    }
    case "multi": {
      const list = Array.isArray(value) ? (value as string[]) : [];
      return (
        <fieldset className="prot-field prot-choice wide">
          <legend>{field.label}</legend>
          <div>
            {field.options!.map((o) => {
              const on = list.includes(o);
              return (
                <button type="button" key={o} className={on ? "on" : undefined} aria-pressed={on} onClick={() => onChange(on ? list.filter((x) => x !== o) : [...list, o])}>
                  {on && <Icon name="check" />}
                  {o}
                </button>
              );
            })}
          </div>
        </fieldset>
      );
    }
    case "check":
      return (
        <div className="prot-check wide">
          <span>
            {field.label}
            {hint}
          </span>
          <CheckToggle value={s} onChange={onChange} label={field.label} />
        </div>
      );
    case "signature":
      return (
        <div className="prot-field wide">
          {label}
          <SignatureField field={field} value={isSignature(value) ? value : null} suggestedName="" onChange={onChange} />
        </div>
      );
    case "table":
      return <TableField field={field} rows={Array.isArray(value) ? (value as Row[]) : []} onChange={onChange} />;
    case "photos":
      return (
        <div className="prot-field wide">
          {label}
          <PhotosField projectId={projectId} value={Array.isArray(value) ? (value as PhotoRef[]) : []} onChange={onChange} />
          {hint}
        </div>
      );
  }
  return null;
}

function CheckToggle({ value, onChange, label, compact }: { value: string; onChange: (v: string) => void; label: string; compact?: boolean }) {
  return (
    <div className={`prot-tri${compact ? " compact" : ""}`} role="radiogroup" aria-label={label}>
      {(["ok", "mangel", "entfaellt"] as CheckValue[]).map((o) => (
        <button key={o} type="button" role="radio" aria-checked={value === o} className={`${o}${value === o ? " on" : ""}`} onClick={() => onChange(value === o ? "" : o)}>
          {CHECK_LABELS[o]}
        </button>
      ))}
    </div>
  );
}

/* ---------- Tabellen ---------- */

function TableField({ field, rows, onChange }: { field: FieldDef; rows: Row[]; onChange: (v: ProtocolValue) => void }) {
  const cols = field.columns ?? [];
  const update = (i: number, key: string, v: unknown) => onChange(rows.map((r, k) => (k === i ? { ...r, [key]: v } : r)));
  const hasSignature = cols.some((c) => c.type === "signature");
  return (
    <div className="prot-field wide prot-table">
      <span className="prot-label">{field.label}</span>
      {field.hint && <span className="prot-hint">{field.hint}</span>}
      {hasSignature && field.confirm && <p className="prot-confirm">{field.confirm}</p>}
      <ol className="prot-rows">
        {rows.map((r, i) => (
          <li key={i} className="prot-rowcard">
            <div className="prot-rowhead">
              <span>{String(i + 1).padStart(2, "0")}</span>
              <button type="button" className="btn-text" onClick={() => onChange([...rows.slice(0, i + 1), { ...r, ...blankSignatures(cols) }, ...rows.slice(i + 1)])}>
                Kopieren
              </button>
              <button type="button" className="btn-text danger" onClick={() => onChange(rows.filter((_, k) => k !== i))} aria-label={`Zeile ${i + 1} entfernen`}>
                Entfernen
              </button>
            </div>
            <div className="prot-cells">
              {cols.map((c) => (
                <Cell key={c.key} col={c} value={r[c.key]} row={r} onChange={(v) => update(i, c.key, v)} />
              ))}
            </div>
          </li>
        ))}
      </ol>
      <button type="button" className="app-btn ghost small prot-addrow" onClick={() => onChange([...rows, {}])}>
        <Icon name="plus" /> Zeile hinzufügen
      </button>
    </div>
  );
}

function blankSignatures(cols: ColumnDef[]) {
  return Object.fromEntries(cols.filter((c) => c.type === "signature").map((c) => [c.key, null]));
}

function Cell({ col, value, row, onChange }: { col: ColumnDef; value: unknown; row: Row; onChange: (v: unknown) => void }) {
  const s = typeof value === "string" ? value : "";
  const label = (
    <span className="prot-cell-label">
      {col.label}
      {col.unit && <em> {col.unit}</em>}
    </span>
  );
  switch (col.type) {
    case "check":
      return (
        <div className="prot-cell check">
          {label}
          <CheckToggle value={s} onChange={onChange} label={col.label} compact />
        </div>
      );
    case "choice":
      return (
        <label className="prot-cell">
          {label}
          <select value={s} onChange={(e) => onChange(e.target.value)}>
            <option value="">–</option>
            {col.options!.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>
      );
    case "signature": {
      const name = typeof row.name === "string" ? row.name : "";
      return (
        <div className="prot-cell sign">
          {label}
          <SignatureField field={{ key: col.key, label: col.label, type: "signature" }} value={isSignature(value) ? value : null} suggestedName={name} onChange={onChange} compact />
        </div>
      );
    }
    case "number":
      return (
        <label className={`prot-cell${outsideLimit(col.limit, s) ? " warn" : ""}`}>
          {label}
          <input inputMode="decimal" value={s} maxLength={40} onChange={(e) => onChange(e.target.value)} title={col.limit ? `Grenzwert ${col.limit.label}` : undefined} />
        </label>
      );
    default:
      return (
        <label className={`prot-cell${(col.width ?? 1) >= 2 ? " wide" : ""}`}>
          {label}
          <input type={col.type === "date" ? "date" : "text"} value={s} maxLength={500} onChange={(e) => onChange(e.target.value)} />
        </label>
      );
  }
}

/* ---------- Unterschrift ---------- */

function SignatureField(props: { field: FieldDef; value: Signature | null; suggestedName: string; onChange: (v: Signature | null) => void; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const { value } = props;
  return (
    <div className={`prot-sign${props.compact ? " compact" : ""}`}>
      {value ? (
        <div className="prot-signed">
          <img src={value.image} alt={`Unterschrift ${value.name}`} />
          <span>
            <b>{value.name || props.field.label}</b>
            <small>{new Date(value.signedAt).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</small>
          </span>
          <button type="button" className="btn-text danger" onClick={() => props.onChange(null)}>
            Entfernen
          </button>
        </div>
      ) : (
        <button type="button" className="prot-signbtn" onClick={() => setOpen(true)}>
          <Icon name="pen" /> Unterschreiben
        </button>
      )}
      {!props.compact && props.field.confirm && <p className="prot-confirm">{props.field.confirm}</p>}
      {open && (
        <SignatureSheet
          title={props.field.label}
          confirm={props.field.confirm}
          initialName={props.suggestedName}
          onClose={() => setOpen(false)}
          onSigned={(s) => {
            setOpen(false);
            props.onChange(s);
          }}
        />
      )}
    </div>
  );
}

export function SignatureSheet(props: { title: string; confirm?: string; initialName: string; onClose: () => void; onSigned: (s: Signature) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [name, setName] = useState(props.initialName);
  const [empty, setEmpty] = useState(true);
  const last = useRef<{ x: number; y: number } | null>(null);
  const bounds = useRef({ x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity });

  useEffect(() => {
    const c = canvas.current!;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.clientWidth * ratio;
    c.height = c.clientHeight * ratio;
    const ctx = c.getContext("2d")!;
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#14213D";
    ctx.lineWidth = 2.4;
  }, []);

  const pos = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const mark = (p: { x: number; y: number }) => {
    const b = bounds.current;
    b.x0 = Math.min(b.x0, p.x);
    b.y0 = Math.min(b.y0, p.y);
    b.x1 = Math.max(b.x1, p.x);
    b.y1 = Math.max(b.y1, p.y);
  };

  function down(e: ReactPointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    last.current = pos(e);
    mark(last.current);
    const ctx = e.currentTarget.getContext("2d")!;
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 1.1, 0, Math.PI * 2);
    ctx.fillStyle = "#14213D";
    ctx.fill();
  }
  function move(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (!last.current) return;
    const ctx = e.currentTarget.getContext("2d")!;
    const events = "getCoalescedEvents" in e.nativeEvent ? e.nativeEvent.getCoalescedEvents() : [];
    const points = events.length ? events.map((ev) => ({ x: ev.clientX - e.currentTarget.getBoundingClientRect().left, y: ev.clientY - e.currentTarget.getBoundingClientRect().top })) : [pos(e)];
    for (const p of points) {
      const mid = { x: (last.current.x + p.x) / 2, y: (last.current.y + p.y) / 2 };
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.quadraticCurveTo(last.current.x, last.current.y, mid.x, mid.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last.current = p;
      mark(p);
    }
    setEmpty(false);
  }
  const up = () => {
    last.current = null;
  };

  function clear() {
    const c = canvas.current!;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    bounds.current = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
    setEmpty(true);
  }

  /** Auf die Unterschrift zuschneiden und verkleinern, damit sie klein bleibt (wenige KB). */
  function take() {
    const c = canvas.current!;
    const ratio = c.width / c.clientWidth;
    const b = bounds.current;
    const pad = 6;
    const sx = Math.max(0, (b.x0 - pad) * ratio);
    const sy = Math.max(0, (b.y0 - pad) * ratio);
    const sw = Math.min(c.width - sx, (b.x1 - b.x0 + pad * 2) * ratio);
    const sh = Math.min(c.height - sy, (b.y1 - b.y0 + pad * 2) * ratio);
    const scale = Math.min(1, 600 / sw, 220 / sh);
    const out = document.createElement("canvas");
    out.width = Math.max(1, Math.round(sw * scale));
    out.height = Math.max(1, Math.round(sh * scale));
    out.getContext("2d")!.drawImage(c, sx, sy, sw, sh, 0, 0, out.width, out.height);
    props.onSigned({ image: out.toDataURL("image/png"), name: name.trim(), signedAt: new Date().toISOString() });
  }

  return (
    <Sheet title={props.title} onClose={props.onClose}>
      <div className="app-form prot-signsheet">
        {props.confirm && <p className="prot-confirm">{props.confirm}</p>}
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="name" placeholder="Vor- und Nachname" />
        </label>
        <div className="prot-pad">
          <canvas
            ref={canvas}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            aria-label="Unterschriftenfeld: mit Finger, Stift oder Maus unterschreiben"
          />
          <span className="prot-padline" aria-hidden="true" />
          {empty && <span className="prot-padhint">Hier mit Finger, Stift oder Maus unterschreiben</span>}
        </div>
        <p className="muted-s">{SIGNATURE_NOTE}</p>
        <div className="prot-finish">
          <button type="button" className="app-btn ghost" onClick={clear} disabled={empty}>
            Neu beginnen
          </button>
          <button type="button" className="app-btn" onClick={take} disabled={empty || !name.trim()}>
            Unterschrift übernehmen
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/* ---------- Fotos ---------- */

function PhotosField({ projectId, value, onChange }: { projectId: string; value: PhotoRef[]; onChange: (v: PhotoRef[]) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="prot-photos">
      {value.length > 0 && (
        <ul>
          {value.map((p, i) => (
            <li key={p.fileId}>
              <img src={`/api/files/${p.fileId}/thumb`} onError={(e) => (e.currentTarget.src = `/api/files/${p.fileId}/content`)} alt="" loading="lazy" />
              <input
                value={p.caption}
                maxLength={200}
                placeholder="Beschriftung, z. B. Abschottung fehlt"
                aria-label={`Beschriftung Foto ${i + 1}`}
                onChange={(e) => onChange(value.map((x) => (x.fileId === p.fileId ? { ...x, caption: e.target.value } : x)))}
              />
              <button type="button" className="ab-btn small" onClick={() => onChange(value.filter((x) => x.fileId !== p.fileId))} aria-label="Foto entfernen">
                <Icon name="close" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="app-btn ghost small" onClick={() => setOpen(true)}>
        <Icon name="camera" /> Fotos aus dem Projekt wählen
      </button>
      {open && <PhotoPicker projectId={projectId} chosen={value} onClose={() => setOpen(false)} onDone={(v) => (setOpen(false), onChange(v))} />}
    </div>
  );
}

function PhotoPicker({ projectId, chosen, onClose, onDone }: { projectId: string; chosen: PhotoRef[]; onClose: () => void; onDone: (v: PhotoRef[]) => void }) {
  const [photos, setPhotos] = useState<ProjectPhoto[] | null>(null);
  const [error, setError] = useState("");
  const [sel, setSel] = useState<PhotoRef[]>(chosen);
  useEffect(() => {
    api
      .get<ProjectPhoto[]>(`/api/projects/${projectId}/photos`)
      .then(setPhotos)
      .catch((err) => setError(errorText(err)));
  }, [projectId]);
  const toggle = (id: string) => setSel((s) => (s.some((x) => x.fileId === id) ? s.filter((x) => x.fileId !== id) : [...s, { fileId: id, caption: "" }]));
  return (
    <Sheet title="Fotos wählen" onClose={onClose}>
      <Message>{error}</Message>
      {photos?.length === 0 && <p className="muted-s">In diesem Projekt gibt es noch keine Fotos. Fotografieren Sie mit dem Handy direkt in einen Ordner, dann erscheinen sie hier.</p>}
      <div className="prot-pick">
        {photos?.map((p) => {
          const n = sel.findIndex((x) => x.fileId === p.id);
          return (
            <button key={p.id} type="button" className={n >= 0 ? "on" : undefined} onClick={() => toggle(p.id)} aria-pressed={n >= 0} title={`${p.name} · ${splitNumber(p.folderName).label}`}>
              <img src={`/api/files/${p.id}/${p.hasThumb ? "thumb" : "content"}`} alt={p.name} loading="lazy" />
              {n >= 0 && <span>{n + 1}</span>}
            </button>
          );
        })}
      </div>
      <button className="app-btn prot-pickdone" onClick={() => onDone(sel)}>
        {sel.length ? `${sel.length} ${sel.length === 1 ? "Foto" : "Fotos"} übernehmen` : "Ohne Fotos"}
      </button>
    </Sheet>
  );
}

/* ---------- Abschließen ---------- */

function CompleteSheet(props: { protocol: ProtocolDetail; template: ProtocolTemplate; data: ProtocolData; projectId: string; onClose: () => void; onDone: (d: ProtocolDetail) => void }) {
  const missing = useMemo(() => missingForCompletion(props.template, props.data), [props.template, props.data]);
  const [folders, setFolders] = useState<FolderInfo[]>([]);
  const [folderId, setFolderId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .get<ProjectDetail>(`/api/projects/${props.projectId}`)
      .then((p) => setFolders(p.folders))
      .catch(() => undefined);
  }, [props.projectId]);

  const options = useMemo(() => {
    const out: { id: string; label: string }[] = [];
    const walk = (parent: string | null, depth: number) => {
      for (const f of folders.filter((x) => x.parentId === parent)) {
        out.push({ id: f.id, label: `${"  ".repeat(depth)}${f.name}` });
        walk(f.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [folders]);

  async function complete() {
    setBusy(true);
    setError("");
    try {
      props.onDone(await api.post<ProtocolDetail>(`/api/protocols/${props.protocol.id}/complete`, folderId ? { folderId } : {}));
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet title="Abschließen" onClose={props.onClose}>
      <div className="app-form">
        {missing.length > 0 ? (
          <>
            <p>Zum Abschließen fehlt noch:</p>
            <ul className="prot-missing">
              {missing.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
            <button className="app-btn" onClick={props.onClose}>
              Weiter ausfüllen
            </button>
          </>
        ) : (
          <>
            <p>Das Protokoll wird als PDF im Projekt abgelegt und lässt sich danach nicht mehr ändern. Für die nächste Prüfung legen Sie einfach eine Kopie an.</p>
            <label>
              Ablegen in
              <select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
                <option value="">Passenden Ordner automatisch wählen</option>
                {options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <p className="prot-legal">{PROTOCOL_DISCLAIMER}</p>
            <Message>{error}</Message>
            <button className="app-btn" onClick={() => void complete()} disabled={busy}>
              {busy ? "PDF wird erstellt …" : "Abschließen und PDF ablegen"}
            </button>
          </>
        )}
      </div>
    </Sheet>
  );
}

function CompletedView({ p, template, projectId, base }: { p: ProtocolDetail; template: ProtocolTemplate; projectId: string; base: string }) {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [sharing, setSharing] = useState(false);
  const [openPdf, setOpenPdf] = useState(false);
  const folderBase = base.replace(/\/protokolle$/, "");
  async function copy() {
    try {
      const d = await api.post<ProtocolDetail>(`/api/projects/${projectId}/protocols`, { copyFrom: p.id });
      navigate(`${base}/${d.id}`);
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <main className="app-main prot-main">
      {openPdf && p.fileId && <PdfViewer url={`/api/files/${p.fileId}/content`} title={p.title} onClose={() => setOpenPdf(false)} />}
      <div className="prot-top">
        <Link className="btn-text prot-back" to={base}>
          <Icon name="back" /> Alle Protokolle
        </Link>
      </div>
      <div className="prot-donecard">
        <span className="prot-doneico">
          <Icon name="check" />
        </span>
        <small>{template.name}</small>
        <h2>{p.title}</h2>
        <p>
          Abgeschlossen am {p.completedAt ? new Date(p.completedAt).toLocaleString("de-DE", { dateStyle: "long", timeStyle: "short" }) : ""}. Das PDF liegt im Projektordner
          und ist unveränderlich.
        </p>
        <div className="prot-finish">
          {p.fileId && (
            <button className="app-btn" onClick={() => setOpenPdf(true)}>
              <Icon name="file" /> PDF öffnen
            </button>
          )}
          {p.fileId && (
            <button className="app-btn ghost" onClick={() => setSharing(true)}>
              <Icon name="send" /> An Kunden senden
            </button>
          )}
          {p.folderId && (
            <Link className="app-btn ghost" to={`${folderBase}/f/${p.folderId}`}>
              <Icon name="folder" /> Zum Ordner
            </Link>
          )}
        </div>
        <button className="btn-text" onClick={() => void copy()}>
          Kopie für die nächste Prüfung anlegen
        </button>
        {p.contentHash && <p className="prot-hash">Prüfsumme {p.contentHash.slice(0, 16)}…</p>}
      </div>
      <Message>{error}</Message>
      {sharing && <ShareSheet projectId={projectId} protocols={[p]} preselect={p.id} onClose={() => setSharing(false)} />}
    </main>
  );
}

